import { useState } from "react";
import {
  FileText,
  TrendingUp,
  Users,
  Wallet,
  Download,
  Calendar,
  ChevronRight,
  X,
  BarChart3,
  PieChart,
  Activity,
  ArrowLeft,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface ReportCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  lastGenerated: string;
  size: string;
}

const reportCards: ReportCard[] = [
  {
    id: "financial",
    title: "Financial Report",
    description: "Revenue, expenses, and profit analysis",
    icon: TrendingUp,
    color: "text-emerald-500",
    lastGenerated: "2024-12-20",
    size: "2.4 MB",
  },
  {
    id: "users",
    title: "User Activity Report",
    description: "User growth, engagement, and retention",
    icon: Users,
    color: "text-blue-500",
    lastGenerated: "2024-12-19",
    size: "1.8 MB",
  },
  {
    id: "trading",
    title: "Trading Volume Report",
    description: "Trade statistics and volume analysis",
    icon: BarChart3,
    color: "text-violet-500",
    lastGenerated: "2024-12-18",
    size: "3.2 MB",
  },
  {
    id: "wallet",
    title: "Wallet Holdings Report",
    description: "Asset distribution and holdings summary",
    icon: Wallet,
    color: "text-amber-500",
    lastGenerated: "2024-12-17",
    size: "1.5 MB",
  },
  {
    id: "kyc",
    title: "KYC Compliance Report",
    description: "Verification status and compliance metrics",
    icon: FileText,
    color: "text-cyan-500",
    lastGenerated: "2024-12-16",
    size: "890 KB",
  },
  {
    id: "system",
    title: "System Performance Report",
    description: "Uptime, errors, and performance metrics",
    icon: Activity,
    color: "text-red-500",
    lastGenerated: "2024-12-15",
    size: "2.1 MB",
  },
];

const AdminReports = () => {
  const [selectedReport, setSelectedReport] = useState<ReportCard | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [dateRange, setDateRange] = useState("30d");
  const [isGenerating, setIsGenerating] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleViewReport = (report: ReportCard) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  const handleDownload = (report: ReportCard) => {
    toast({
      title: "Downloading Report",
      description: `${report.title} is being downloaded...`,
    });
    setTimeout(() => {
      toast({
        title: "Download Complete",
        description: `${report.title} has been downloaded.`,
      });
    }, 1500);
  };

  const handleGenerateReport = async (reportId: string) => {
    setIsGenerating(reportId);
    toast({
      title: "Generating Report",
      description: "Please wait while we generate your report...",
    });

    setTimeout(() => {
      setIsGenerating(null);
      toast({
        title: "Report Generated",
        description: "Your report is ready for download.",
      });
    }, 2000);
  };

  const handleExportAll = () => {
    toast({
      title: "Exporting All Reports",
      description: "Preparing all reports for download...",
    });
    setTimeout(() => {
      toast({
        title: "Export Complete",
        description: "All reports have been exported as a zip file.",
      });
    }, 2000);
  };

  // Mock report data
  const getReportData = (reportId: string) => {
    switch (reportId) {
      case "financial":
        return {
          summary: [
            { label: "Total Revenue", value: "$2,456,789", change: "+15.2%" },
            { label: "Total Expenses", value: "$1,234,567", change: "+8.5%" },
            { label: "Net Profit", value: "$1,222,222", change: "+22.1%" },
            { label: "Profit Margin", value: "49.7%", change: "+3.2%" },
          ],
          details: [
            {
              category: "Trading Fees",
              amount: "$1,234,567",
              percentage: "50.2%",
            },
            {
              category: "Withdrawal Fees",
              amount: "$456,789",
              percentage: "18.6%",
            },
            {
              category: "Deposit Fees",
              amount: "$234,567",
              percentage: "9.5%",
            },
            { category: "Forex Fees", amount: "$530,866", percentage: "21.6%" },
          ],
        };
      case "users":
        return {
          summary: [
            { label: "Total Users", value: "38,542", change: "+14.2%" },
            { label: "Active Users", value: "24,521", change: "+18.5%" },
            { label: "New Users", value: "4,821", change: "+22.3%" },
            { label: "Churn Rate", value: "2.1%", change: "-0.5%" },
          ],
          details: [
            {
              category: "Verified Users",
              amount: "28,456",
              percentage: "73.8%",
            },
            { category: "Pending KYC", amount: "5,234", percentage: "13.6%" },
            { category: "Unverified", amount: "4,852", percentage: "12.6%" },
          ],
        };
      case "trading":
        return {
          summary: [
            { label: "Total Volume", value: "$45.2M", change: "+12.8%" },
            { label: "Total Trades", value: "156,234", change: "+18.2%" },
            { label: "Avg Trade Size", value: "$289", change: "-3.1%" },
            { label: "Success Rate", value: "98.7%", change: "+0.3%" },
          ],
          details: [
            { category: "Spot Trading", amount: "$28.5M", percentage: "63.1%" },
            { category: "P2P Trading", amount: "$12.3M", percentage: "27.2%" },
            { category: "Forex", amount: "$4.4M", percentage: "9.7%" },
          ],
        };
      default:
        return {
          summary: [
            { label: "Total Records", value: "12,456", change: "+8.5%" },
            { label: "Processed", value: "11,892", change: "+10.2%" },
            { label: "Pending", value: "564", change: "-15.3%" },
          ],
          details: [
            { category: "Category A", amount: "5,234", percentage: "42.0%" },
            { category: "Category B", amount: "4,123", percentage: "33.1%" },
            { category: "Category C", amount: "3,099", percentage: "24.9%" },
          ],
        };
    }
  };

  return (
    <div className="min-h-screen bg-background lg:px-6">
      <div className="max-w-7xl mx-auto">
        <Button
          className="hidden lg:flex"
          variant="ghost"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Reports</h1>
            <p className="text-muted-foreground mt-1">
              Generate and download platform reports
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
            <button
              onClick={handleExportAll}
              className="flex items-center gap-2 px-4 py-2 bg-card text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4" />
              Export All
            </button>
          </div>
        </div>

        {/* Report Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportCards.map((report) => {
            const Icon = report.icon;
            return (
              <div
                key={report.id}
                className="nex-card p-6 hover:border-primary/30 transition-colors cursor-pointer group"
                onClick={() => handleViewReport(report)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                    <Icon className={`w-6 h-6 ${report.color}`} />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(report);
                    }}
                    className="p-2 text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </div>

                <h3 className="font-semibold text-foreground text-lg mb-1">
                  {report.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {report.description}
                </p>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{report.lastGenerated}</span>
                  </div>
                  <span>{report.size}</span>
                </div>

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateReport(report.id);
                    }}
                    disabled={isGenerating === report.id}
                    className="text-sm text-primary hover:underline disabled:opacity-50"
                  >
                    {isGenerating === report.id
                      ? "Generating..."
                      : "Regenerate"}
                  </button>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 nex-card p-6">
          <h3 className="font-semibold text-foreground text-lg mb-4">
            Report Statistics
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold text-foreground">156</p>
              <p className="text-xs text-muted-foreground">Reports Generated</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold text-foreground">45.2 MB</p>
              <p className="text-xs text-muted-foreground">Total Size</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold text-foreground">24</p>
              <p className="text-xs text-muted-foreground">Downloads Today</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold text-foreground">98.5%</p>
              <p className="text-xs text-muted-foreground">Success Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Detail Modal */}
      {showReportModal && selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <selectedReport.icon
                    className={`w-5 h-5 ${selectedReport.color}`}
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    {selectedReport.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedReport.description}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowReportModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Report Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {getReportData(selectedReport.id).summary.map(
                (item: any, idx: number) => (
                  <div key={idx} className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">
                      {item.label}
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      {item.value}
                    </p>
                    <p
                      className={`text-xs ${item.change.startsWith("+") ? "text-emerald-500" : "text-red-500"}`}
                    >
                      {item.change}
                    </p>
                  </div>
                ),
              )}
            </div>

            {/* Report Details Table */}
            <div className="border border-border rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left text-xs font-medium text-muted-foreground py-3 px-4">
                      Category
                    </th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">
                      Amount
                    </th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-4">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getReportData(selectedReport.id).details.map(
                    (item: any, idx: number) => (
                      <tr
                        key={idx}
                        className="border-t border-border hover:bg-muted/50 transition-colors"
                      >
                        <td className="py-3 px-4 text-foreground">
                          {item.category}
                        </td>
                        <td className="text-right py-3 px-4 text-foreground font-mono">
                          {item.amount}
                        </td>
                        <td className="text-right py-3 px-4">
                          <span className="px-2 py-1 rounded bg-primary/10 text-primary text-xs">
                            {item.percentage}
                          </span>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => handleDownload(selectedReport)}
                className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:brightness-110 transition-all flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Report
              </button>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  handleGenerateReport(selectedReport.id);
                }}
                className="flex-1 py-2 border border-border text-foreground rounded-lg font-medium hover:bg-muted transition-all"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
